/* eslint-disable i18next/no-literal-string */
'use client'

import type { UseEmblaCarouselType } from 'embla-carousel-react'
import useEmblaCarousel from 'embla-carousel-react'
import * as React from 'react'

import { CaretLeft } from '@/components/icons/CaretLeft'
import { CaretRight } from '@/components/icons/CaretRight'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/styles'

type CarouselApi = UseEmblaCarouselType[1]
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>
type CarouselOptions = UseCarouselParameters[0]
type CarouselPlugin = UseCarouselParameters[1]

type CarouselProps = {
	opts?: CarouselOptions
	plugins?: CarouselPlugin
	orientation?: 'horizontal' | 'vertical'
	setApi?: (api: CarouselApi) => void
}

type CarouselContextProps = {
	carouselRef: ReturnType<typeof useEmblaCarousel>[0]
	api: ReturnType<typeof useEmblaCarousel>[1]
	scrollPrev: () => void
	scrollNext: () => void
	canScrollPrev: boolean
	canScrollNext: boolean
} & CarouselProps

const CarouselContext = React.createContext<CarouselContextProps | null>(null)

function useCarousel() {
	const context = React.useContext(CarouselContext)

	if (!context) {
		throw new Error('useCarousel must be used within a <Carousel />')
	}

	return context
}

const Carousel = React.forwardRef<
	HTMLDivElement,
	React.HTMLAttributes<HTMLDivElement> & CarouselProps
>(({ orientation = 'horizontal', opts, setApi, plugins, className, children, ...props }, ref) => {
	const [canScrollPrev, setCanScrollPrev] = React.useState(false)
	const [canScrollNext, setCanScrollNext] = React.useState(false)

	const [carouselRef, api] = useEmblaCarousel(
		{
			...opts,
			axis: orientation === 'horizontal' ? 'x' : 'y',
			watchDrag: opts?.watchDrag ?? (canScrollPrev || canScrollNext),
		},
		plugins,
	)

	const onSelect = React.useCallback((api: CarouselApi) => {
		if (!api) {
			return
		}

		setCanScrollPrev(api.canScrollPrev())
		setCanScrollNext(api.canScrollNext())
	}, [])

	const scrollPrev = React.useCallback(() => {
		api?.scrollPrev()
	}, [api])

	const scrollNext = React.useCallback(() => {
		api?.scrollNext()
	}, [api])

	const handleKeyDown = React.useCallback(
		(event: React.KeyboardEvent<HTMLDivElement>) => {
			if (event.key === 'ArrowLeft') {
				event.preventDefault()
				scrollPrev()
			} else if (event.key === 'ArrowRight') {
				event.preventDefault()
				scrollNext()
			}
		},
		[scrollPrev, scrollNext],
	)

	React.useEffect(() => {
		if (!api || !setApi) {
			return
		}

		setApi(api)
	}, [api, setApi])

	React.useEffect(() => {
		if (!api) {
			return
		}

		onSelect(api)
		api.on('reInit', onSelect)
		api.on('select', onSelect)

		return () => {
			api?.off('select', onSelect)
		}
	}, [api, onSelect])

	return (
		<CarouselContext.Provider
			value={{
				carouselRef,
				api: api,
				opts,
				orientation: orientation || (opts?.axis === 'y' ? 'vertical' : 'horizontal'),
				scrollPrev,
				scrollNext,
				canScrollPrev,
				canScrollNext,
			}}
		>
			{/* biome-ignore lint/a11y/useAriaPropsSupportedByRole: aria-roledescription is valid for carousel pattern */}
			<section
				ref={ref}
				onKeyDownCapture={handleKeyDown}
				className={cn('relative', className)}
				aria-roledescription="carousel"
				{...props}
			>
				{children}
			</section>
		</CarouselContext.Provider>
	)
})
Carousel.displayName = 'Carousel'

const CarouselContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
	({ className, ...props }, ref) => {
		const { carouselRef, orientation } = useCarousel()

		return (
			<div ref={carouselRef} className="overflow-hidden">
				<div
					ref={ref}
					className={cn(
						'flex',
						orientation === 'horizontal' ? '-ml-4' : '-mt-4 flex-col',
						className,
					)}
					{...props}
				/>
			</div>
		)
	},
)
CarouselContent.displayName = 'CarouselContent'

const CarouselItem = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
	({ className, ...props }, ref) => {
		const { orientation } = useCarousel()

		return (
			// biome-ignore lint/a11y/useSemanticElements: div with role="group" is appropriate for carousel slides
			<div
				ref={ref}
				role="group"
				aria-roledescription="slide"
				className={cn(
					'min-w-0 shrink-0 grow-0 basis-full',
					orientation === 'horizontal' ? 'pl-4' : 'pt-4',
					className,
				)}
				{...props}
			/>
		)
	},
)
CarouselItem.displayName = 'CarouselItem'

const CarouselPrevious = React.forwardRef<HTMLButtonElement, React.ComponentProps<typeof Button>>(
	({ className, variant = 'secondary', size = 'none', ...props }, ref) => {
		const { orientation, scrollPrev, canScrollPrev } = useCarousel()

		return (
			<Button
				ref={ref}
				variant={variant}
				size={size}
				className={cn(
					'absolute h-full border-none w-10 bg-[linear-gradient(90deg,rgba(255,255,255,1)_0%,rgba(255,255,255,0)_100%)] rounded-none transition-opacity duration-300 ease-in-out',
					orientation === 'horizontal'
						? 'left-0 top-1/2 -translate-y-1/2'
						: '-top-12 left-1/2 -translate-x-1/2 rotate-90',
					className,
					!canScrollPrev && 'opacity-0!',
				)}
				disabled={!canScrollPrev}
				onClick={scrollPrev}
				{...props}
			>
				<CaretLeft className="size-5 -ml-4" />
				<span className="sr-only">Previous slide</span>
			</Button>
		)
	},
)
CarouselPrevious.displayName = 'CarouselPrevious'

const CarouselNext = React.forwardRef<HTMLButtonElement, React.ComponentProps<typeof Button>>(
	({ className, variant = 'secondary', size = 'none', ...props }, ref) => {
		const { orientation, scrollNext, canScrollNext } = useCarousel()

		return (
			<Button
				ref={ref}
				variant={variant}
				size={size}
				className={cn(
					'absolute h-full border-none w-10 bg-[linear-gradient(270deg,rgba(255,255,255,1)_0%,rgba(255,255,255,0)_100%)] rounded-none transition-opacity duration-300 ease-in-out',
					orientation === 'horizontal'
						? 'right-0 top-1/2 -translate-y-1/2'
						: '-bottom-12 left-1/2 -translate-x-1/2 rotate-90',
					className,
					!canScrollNext && 'opacity-0!',
				)}
				disabled={!canScrollNext}
				onClick={scrollNext}
				{...props}
			>
				<CaretRight className="size-5 -mr-4" />
				<span className="sr-only">Next slide</span>
			</Button>
		)
	},
)
CarouselNext.displayName = 'CarouselNext'

export { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, type CarouselApi }
